import { ChangeDetectionStrategy, Component, computed, input, linkedSignal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { Invoice, LineItem } from "../../../../../pojo/Pricing";
import { S25PricingUtil } from "../../../s25.pricing.util";
import { InvoiceMove } from "./invoice-move";
import { invoiceDisplayName } from "../../s25.event.pricing.store";
import {
    AdjustmentAllocation,
    emptyAllocation,
    S25AdjustmentAmountFieldComponent,
    sameAllocation,
} from "./s25.adjustment.amount.field.component";

type AllocationPair = { source: AdjustmentAllocation; target: AdjustmentAllocation };

type AdjustmentRow = { key: string; lineItem: LineItem; orphaned: boolean };

function allocationOf(lineItem: LineItem | undefined): AdjustmentAllocation {
    if (!lineItem) return { ...emptyAllocation };
    if (lineItem.adjustmentPercent !== undefined) {
        return { amount: lineItem.adjustmentPercent, type: "percent", reason: lineItem.adjustmentName ?? "" };
    }
    return { amount: lineItem.adjustmentAmt ?? 0, type: "amount", reason: lineItem.adjustmentName ?? "" };
}

@TypeManagerDecorator("s25-ng-move-item-adjustments")
@Component({
    selector: "s25-ng-move-item-adjustments",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25AdjustmentAmountFieldComponent],
    template: `
        <p class="ngFinePrint">
            Line item adjustments have been added to
            @if (isOccurrenceMove()) {
                the following items associated with the occurrence on <b>{{ itemLabel() }}</b
                >.
            } @else {
                <b>{{ itemLabel() }}</b
                >.
            }
            These adjustments can be updated to account for the item's transfer between
            <b>{{ sourceName() }}</b> and <b>{{ targetName() }}</b
            >.
        </p>

        @if (eventTypeExcluded()) {
            <p class="cn-alert cn-alert--warning">
                Event type line items, if any, do not appear below because they are not included in&nbsp;<b>{{
                    targetName()
                }}</b
                >.
            </p>
        }

        <table class="move-adj">
            <thead>
                <tr>
                    <th scope="col">Line Item</th>
                    <th scope="col">{{ sourceHeader() }}</th>
                    <th scope="col">{{ targetHeader() }}</th>
                </tr>
            </thead>
            <tbody>
                @for (row of rows(); track row.key) {
                    <tr>
                        <th scope="row">
                            {{ row.lineItem.itemName }}
                            @if (row.orphaned) {
                                <p class="ngFinePrint">
                                    Adjustment will be removed from <b>{{ sourceName() }}</b> after moving the
                                    occurrence, as no remaining occurrence includes this line item.
                                </p>
                            }
                        </th>
                        <td>
                            <s25-ng-adjustment-amount-field
                                [allocation]="allocations()[row.key].source"
                                (allocationChange)="setAllocation(row.key, 'source', $event)"
                                [disabled]="sourceDisabled() || row.orphaned"
                                [groupName]="row.key + '-source'"
                            />
                        </td>
                        <td>
                            <s25-ng-adjustment-amount-field
                                [allocation]="allocations()[row.key].target"
                                (allocationChange)="setAllocation(row.key, 'target', $event)"
                                [applyFrom]="allocations()[row.key].source"
                                [applyLabel]="'Apply adjustment from ' + sourceName()"
                                [groupName]="row.key + '-target'"
                            />
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    `,
    styles: `
        :host {
            display: block;
        }

        .cn-alert.cn-alert--warning {
            font-size: 90%;
            margin-block: 5px;
        }

        table {
            width: 100%;
            border-collapse: collapse;
        }

        th,
        td {
            text-align: left;
            vertical-align: top;
            padding: 10px 12px 10px 0;
            border-bottom: 1px solid var(--color--border);
        }

        tbody th {
            font-weight: 600;
            max-width: 16em;
        }
    `,
})
export class S25MoveItemAdjustmentsComponent {
    readonly item = input.required<InvoiceMove.MovableItem>();
    /** The occurrence label, or the requirement's name when a requirement is what moves. */
    readonly itemLabel = input("");
    readonly source = input.required<Invoice>();
    readonly target = input<Invoice | undefined>(undefined);
    /** Names a target that does not exist yet, ie an invoice the user is in the middle of creating. */
    readonly targetLabel = input("");
    /** Overrides what the target bills, for a target whose `billDefn` is still being filled in. */
    readonly targetIncludesEventType = input<boolean | undefined>(undefined);
    readonly adjustedItems = input<LineItem[]>([]);

    protected readonly isOccurrenceMove = computed(() => !!this.item().rsrvId);
    protected readonly sourceName = computed(() => invoiceDisplayName(this.source()));
    protected readonly targetName = computed(() => this.targetLabel() || invoiceDisplayName(this.target()));
    private readonly hasTarget = computed(() => !!this.target() || !!this.targetLabel());
    protected readonly eventTypeExcluded = computed(
        () => !(this.targetIncludesEventType() ?? this.target()?.billDefn?.includeEventType),
    );

    /** Nothing is left to adjust on the invoice being left once its last occurrence goes. */
    protected readonly sourceDisabled = computed(() => {
        const item = this.item();
        if (!item.rsrvId) return false;
        const rsrvIds = InvoiceMove.invoiceObjectIds(this.source()).rsrvIds;
        return rsrvIds.size <= 1 && rsrvIds.has(item.rsrvId);
    });

    protected readonly sourceHeader = computed(
        () => `Adjustment for ${this.sourceName()}${this.sourceDisabled() ? " (No occurrences remaining)" : ""}`,
    );
    protected readonly targetHeader = computed(() => `Adjustment for ${this.targetName()}`);

    protected readonly rows = computed<AdjustmentRow[]>(() => {
        const orphaned = InvoiceMove.orphanedAdjustmentKeys(this.adjustedItems(), this.item(), this.source());
        return this.adjustedItems().map((lineItem) => ({
            key: InvoiceMove.adjustmentKey(lineItem),
            lineItem,
            orphaned: orphaned.has(InvoiceMove.adjustmentKey(lineItem)),
        }));
    });

    private readonly seededAllocations = computed<Record<string, AllocationPair>>(() => {
        const targetItems = this.target()?.billing?.lineItems ?? [];
        const seeds: Record<string, AllocationPair> = {};

        for (const { key, lineItem } of this.rows()) {
            const onTarget = targetItems.find(
                (candidate) => candidate.itemId === lineItem.itemId && candidate.itemType === lineItem.itemType,
            );
            seeds[key] = { source: allocationOf(lineItem), target: allocationOf(onTarget) };
        }
        return seeds;
    });

    protected readonly allocations = linkedSignal(() => this.seededAllocations());

    protected setAllocation(key: string, side: keyof AllocationPair, allocation: AdjustmentAllocation) {
        this.allocations.update((prev) => ({ ...prev, [key]: { ...prev[key], [side]: allocation } }));
    }

    /**
     * Only what the user actually changed, so an untouched row leaves its existing adjustment alone
     * rather than rewriting it.
     */
    readonly adjustmentPayload = computed<LineItem[]>(() => {
        const seeds = this.seededAllocations(),
            edited = this.allocations(),
            target = this.target(),
            payload: LineItem[] = [];

        for (const { key, lineItem } of this.rows()) {
            const seed = seeds[key],
                current = edited[key];
            if (!seed || !current) continue;

            if (!sameAllocation(seed.source, current.source)) {
                payload.push(this.toLineItem(lineItem, this.source().id, current.source));
            }
            // A target still being created has no id yet, so whoever creates it fills one in.
            if (this.hasTarget() && !sameAllocation(seed.target, current.target)) {
                payload.push(this.toLineItem(lineItem, target?.id, current.target));
            }
        }
        return payload;
    });

    private toLineItem(lineItem: LineItem, billId: number | undefined, allocation: AdjustmentAllocation): LineItem {
        return {
            billId,
            itemId: lineItem.itemId,
            itemType: lineItem.itemType,
            chargeToId: lineItem.chargeToId,
            profileId: lineItem.profileId,
            ...(allocation.type === "percent"
                ? { adjustmentPct: S25PricingUtil.roundPercent(allocation.amount) }
                : { adjustmentAmt: allocation.amount }),
            adjustmentName: allocation.amount ? allocation.reason : "",
        };
    }
}
